import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import Pagination from '../../components/UI/Pagination';
import { openAlert } from '../../utils/common';
import {
  CALIBRATION_PHASE_LABEL,
  CALIBRATION_STATUS_LABEL,
  canViewCalibrationMemberInfo,
} from '../../utils/calibrationAdmin';
import calibrationService from '../../services/calibrationService';
import { useAuth } from '../../context/AuthContext';

const pageSize = 20;
const pageBlock = 5;

const CalibrationQueuePage = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const canViewMember = canViewCalibrationMemberInfo(role);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchQueue = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await calibrationService.getQueue(page, pageSize);
      if (res.success) {
        setItems(res.data?.items || []);
        setTotalCount(res.data?.pagination?.totalCount || 0);
      } else {
        openAlert({ title: '알림', description: res.message || '보정 대기열 조회에 실패했습니다.' });
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || '보정 대기열 조회 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue(currentPage);
  }, [currentPage, fetchQueue]);

  const openCase = (caseId) => {
    navigate(`/calibration/cases/${encodeURIComponent(caseId)}`);
  };

  const colCount = canViewMember ? 9 : 8;

  return (
    <SectionContainer title="보정 대기열">
      <p style={{ color: '#666', marginBottom: 16 }}>
        보정이 진행 중이거나 raw 데이터·모델 처리가 필요한 케이스 목록입니다.
        {!canViewMember && (
          <span> 회원 조회 권한이 없어 케이스 ID만 표시됩니다.</span>
        )}
      </p>
      <Spinner visible={loading} />
      <div className="content_area">
        <div className="table_area">
          <div className="table">
            <table className="account-list-table">
              <thead>
                <tr>
                  <th>케이스 ID</th>
                  {canViewMember && <th>회원</th>}
                  <th>상태</th>
                  <th>진행 단계</th>
                  <th>진행률</th>
                  <th>raw 측정</th>
                  <th>모델</th>
                  <th>최근 업데이트</th>
                  <th>처리</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 ? (
                  <tr><td colSpan={colCount}>대기 중인 보정 케이스가 없습니다.</td></tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.case_id}>
                      <td>{item.case_id}</td>
                      {canViewMember && (
                        <td>
                          {item.user_name || '-'}
                          {item.user_id ? ` (${item.user_id})` : ''}
                        </td>
                      )}
                      <td>{CALIBRATION_STATUS_LABEL[item.status] || item.status}</td>
                      <td>{CALIBRATION_PHASE_LABEL[item.phase] || item.phase}</td>
                      <td>{item.total_count}/{item.total_max}</td>
                      <td>{item.raw_pair_count ?? 0}</td>
                      <td>{item.user_model_count}</td>
                      <td>{item.updated_at || '-'}</td>
                      <td>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => openCase(item.case_id)}>
                          처리
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Pagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={pageSize}
        pageBlock={pageBlock}
        onPageChange={setCurrentPage}
      />
    </SectionContainer>
  );
};

export default CalibrationQueuePage;
